import React, { useState } from 'react';
import { PRIORITIES } from '../utils/portal';
import { segmentName } from '../utils/segments';

/**
 * Create / edit form for an action item. Rendered inside a modal by the
 * portal pages. Chair or admin only — the pages guard on that before showing it.
 */
export default function PortalActionForm({
  initial,
  volunteers = [],
  segments = [],
  meetings = [],
  lockMeetingId,
  onSave,
  onCancel,
}) {
  const [form, setForm] = useState({
    title: initial?.title || '',
    details: initial?.details || '',
    assignee_id: initial?.assignee_id || '',
    segment_id: initial?.segment_id || '',
    meeting_id: lockMeetingId || initial?.meeting_id || '',
    due_date: initial?.due_date || '',
    priority: initial?.priority || 'normal',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Give the action item a title'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave({ ...form, title: form.title.trim() });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <form className="portal-action-form" onSubmit={handleSubmit}>
      <h3>{initial ? 'Edit Action Item' : 'New Action Item'}</h3>

      {error && <div className="alert alert-error">{error}</div>}

      <label>
        What needs doing?
        <input
          type="text"
          value={form.title}
          onChange={set('title')}
          placeholder="e.g. Trim Powerline #2 back to the Calle Pimienta junction"
          autoFocus
        />
      </label>

      <label>
        Details <span className="field-hint">(optional)</span>
        <textarea rows={3} value={form.details} onChange={set('details')} />
      </label>

      <div className="portal-form-row">
        <label>
          Assigned to
          <select value={form.assignee_id} onChange={set('assignee_id')}>
            <option value="">Unassigned</option>
            {volunteers.map((v) => (
              <option key={v.id} value={v.id}>{v.display_name}</option>
            ))}
          </select>
        </label>

        <label>
          Due date
          <input type="date" value={form.due_date || ''} onChange={set('due_date')} />
        </label>

        <label>
          Priority
          <select value={form.priority} onChange={set('priority')}>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
      </div>

      <label>
        Trail segment <span className="field-hint">(optional — adds a link straight to the map)</span>
        <select value={form.segment_id} onChange={set('segment_id')}>
          <option value="">No segment</option>
          {segments.map((s) => (
            <option key={s.id} value={s.id}>
              {segmentName(s)}
            </option>
          ))}
        </select>
      </label>

      {!lockMeetingId && (
        <label>
          Meeting
          <select value={form.meeting_id} onChange={set('meeting_id')}>
            <option value="">Not tied to a meeting</option>
            {meetings.map((m) => (
              <option key={m.id} value={m.id}>{m.title}</option>
            ))}
          </select>
        </label>
      )}

      <div className="portal-form-actions">
        <button type="button" className="btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn-sm btn-primary" disabled={saving}>
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Action Item'}
        </button>
      </div>
    </form>
  );
}
